import React from 'react';
import { Smartphone, Battery, Tablet, Laptop, Monitor, Cable, Droplets, Wrench } from 'lucide-react';
import { allServices } from '../mockData';

const iconMap = {
  Smartphone,
  Battery,
  Tablet,
  Laptop,
  Monitor,
  Cable,
  Droplets,
  Wrench
};

const PremiumServicesSection = () => {
  return (
    <section id="services" className="py-32 bg-gradient-to-b from-white via-slate-50 to-white">
      <div className="container mx-auto px-4">
        {/* Section Header - Apple Style */}
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h2 className="text-6xl md:text-7xl font-bold text-slate-900 mb-8 tracking-tight leading-none">
            Expert repair for<br/>all your devices.
          </h2>
          <p className="text-2xl text-slate-600 leading-relaxed font-light">
            From iPhones to MacBooks, we fix them all. Fast, professional, and guaranteed.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {allServices.map((service) => {
            const Icon = iconMap[service.icon];
            return (
              <div 
                key={service.id}
                className="group relative bg-gradient-to-br from-white to-slate-50 rounded-3xl p-8 hover:shadow-2xl hover:shadow-cyan-500/10 transition-all duration-500 border-2 border-slate-100 hover:border-cyan-400 overflow-hidden hover:-translate-y-2"
              >
                {/* Animated background gradient on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 via-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                {/* Glow effect */}
                <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-3xl opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500"></div>
                
                <div className="relative z-10">
                  {/* Icon with enhanced styling */}
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg shadow-cyan-500/30">
                    <Icon className="w-8 h-8 text-white" />
                  </div>

                  {/* Service Name */}
                  <h3 className="text-2xl font-bold text-slate-900 mb-3 leading-tight group-hover:text-cyan-600 transition-colors duration-300">
                    {service.name}
                  </h3>

                  {/* Description */}
                  <p className="text-slate-600 mb-6 leading-relaxed text-sm">
                    {service.description}
                  </p>

                  {/* Time with icon */}
                  {service.time && (
                    <div className="flex items-center space-x-2 text-sm text-slate-500 mb-3 bg-slate-100 rounded-full px-4 py-2 w-fit">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="font-medium">{service.time}</span>
                    </div>
                  )}

                  {/* Devices supported with better styling */}
                  {service.devices && (
                    <div className="text-xs text-slate-500 mt-4 pt-4 border-t border-slate-200 leading-relaxed">
                      {service.devices}
                    </div>
                  )}

                  {/* Get Quote button - appears on hover */}
                  <button 
                    onClick={() => document.getElementById('booking').scrollIntoView({ behavior: 'smooth' })}
                    className="mt-6 w-full bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-semibold py-3 rounded-xl opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300 hover:shadow-lg"
                  >
                    Get Free Quote →
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA Section with enhanced styling */}
        <div className="max-w-4xl mx-auto mt-20 p-12 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-[40px] text-center relative overflow-hidden shadow-2xl">
          {/* Decorative elements */}
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl"></div>
          
          <div className="relative z-10">
            <h3 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Don't see your device?
            </h3>
            <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
              We repair all smartphones, tablets, and laptops. Contact us for a free quote.
            </p>
            <button 
              onClick={() => document.getElementById('booking').scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center px-10 py-5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-full font-semibold text-lg hover:from-cyan-400 hover:to-blue-500 transition-all duration-300 shadow-2xl hover:shadow-cyan-500/50 hover:scale-105"
            >
              Get Free Quote
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PremiumServicesSection;
